import { defer, LoaderFunctionArgs, redirect } from "react-router-dom";
import { getCachedOrFetch } from "@/queryClient";
import { initializeAuthStorage } from "@/hooks/authInit";
import { computeForcedRedirectURL } from "@/routes/RootRoute/forceRedirects";
import { fetchApi } from "@tiny/utils";
import type { GetAccountResponse } from "@/types/api/account";
import type { UserStatusOverview } from "@/contexts/user";

const rootKeys = {
  all: ["root"] as const,
  userStatus: () => [...rootKeys.all, "userStatus"] as const,
  account: () => [...rootKeys.all, "account"] as const,
};

const rootQueryFns = {
  userStatus: (signal?: AbortSignal) =>
    fetchApi<UserStatusOverview>("/authorization-server/user/status/overview", {
      signal,
    }),

  account: (signal?: AbortSignal) =>
    fetchApi<GetAccountResponse>("/accountservice/account", { signal }),
};

const rootQueryConfigs = {
  userStatus: {
    queryKey: rootKeys.userStatus(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      rootQueryFns.userStatus(signal),
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  },

  account: {
    queryKey: rootKeys.account(),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      rootQueryFns.account(signal),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
  },
};

export const rootLoader = async ({ request }: LoaderFunctionArgs) => {
  initializeAuthStorage(new URL(request.url));

  const initialUserStatus = await getCachedOrFetch(
    {
      ...rootQueryConfigs.userStatus,
      staleTime: 0,
    },
    request.signal
  );

  const stillOnboarding =
    initialUserStatus?.onboardingStep &&
    initialUserStatus.onboardingStep !== "FINISHED";

  const userStatus = stillOnboarding
    ? initialUserStatus
    : await getCachedOrFetch(rootQueryConfigs.userStatus, request.signal);

  const redirectUrl =
    userStatus &&
    computeForcedRedirectURL(request.url, userStatus.onboardingStep);

  if (redirectUrl) {
    return redirect(redirectUrl);
  }

  const accountInfo = await getCachedOrFetch(
    rootQueryConfigs.account,
    request.signal
  );

  return defer({
    userStatus,
    accountInfo,
  });
};
